"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = useScrollLock;
var _react = require("react");
var _utils = require("../utils");
/**
 * useScrollLock
 *
 * Prevents page or element scroll when `enabled` is false.
 * Useful for modals, drawers, or fixed UI states.
 *
 * @param {Options} Options - Configuration options
 * @param {boolean} Options.enabled - If `false`, disables scrolling. If `true`, allows scrolling.
 * @param {HTMLElement | Document} [options.target=document] - Optional scroll target (default is entire document).
 * @param {boolean} [Options.elementOnly=false] - If `true`, uses `overflow: hidden` on the target instead of event listeners.
 *
 * @example
 * ```tsx
 * useScrollLock({ enabled: false }) // disables page scroll
 * useScrollLock({ enabled: true })  // restores scroll
 * ```
 *
 * @example
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null)
 * useScrollLock({ enabled: false, target: ref.current })
 * ```
 *
 * @example
 * ```tsx
 * useScrollLock({ enabled: false, target: ref.current, elementOnly: true }) // disables scroll with overflow: hidden
 * ```
 */
function useScrollLock(options) {
  const {
    enabled,
    target = document,
    elementOnly = false
  } = options;
  (0, _react.useEffect)(() => {
    if (!(0, _utils.isBrowser)()) return;
    const el = target;
    if (!el) return;
    if (elementOnly) {
      const originalOverflow = el.style.overflow;
      if (!enabled) {
        el.style.overflow = 'hidden';
      } else {
        el.style.overflow = originalOverflow;
      }
      return () => {
        el.style.overflow = originalOverflow;
      };
    } else {
      const handleScroll = e => e.preventDefault();
      if (!enabled) {
        el.addEventListener('wheel', handleScroll, {
          passive: false
        });
        el.addEventListener('touchmove', handleScroll, {
          passive: false
        });
      } else {
        el.removeEventListener('wheel', handleScroll);
        el.removeEventListener('touchmove', handleScroll);
      }
      return () => {
        el.removeEventListener('wheel', handleScroll);
        el.removeEventListener('touchmove', handleScroll);
      };
    }
  }, [enabled, target, elementOnly]);
}